<template>
  <q-dialog
    v-model="dialogOpen"
    persistent
    maximized
  >
    <q-card style="max-width: 800px; margin: auto">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ props.i18n.initial_balance_dialog_title }}
        </div>
        <q-space />
        <q-btn
          icon="close"
          flat
          round
          dense
          @click="close"
        />
      </q-card-section>

      <q-card-section>
        <q-banner
          class="bg-pastel-red q-pa-md q-mb-lg"
          rounded
        >
          <template v-slot:avatar>
            <q-icon
              name="warning"
              color="negative"
              size="lg"
            />
          </template>
          <div class="text-subtitle1 text-bold q-mb-sm">
            {{ props.i18n.initial_balance_warning_title }}
          </div>
          <div>
            {{ props.i18n.initial_balance_warning_description }}
            <ul class="q-mt-sm q-mb-none">
              <li>{{ props.i18n.initial_balance_warning_point1 }}</li>
              <li>{{ props.i18n.initial_balance_warning_point2 }}</li>
              <li>{{ props.i18n.initial_balance_warning_point3 }}</li>
            </ul>
          </div>
        </q-banner>

        <div class="q-gutter-md">
          <div>
            <div class="text-input-label q-mb-sm">
              {{ props.i18n.initial_balance_date_label }}
            </div>
            <q-input
              v-model="selectedDate"
              outlined
              dense
              mask="date"
              :hint="props.i18n.initial_balance_date_hint"
              :rules="[
                'date',
                val => !!val || props.i18n.initial_balance_date_required,
                val => {
                  const today = new Date();
                  today.setHours(23, 59, 59, 999);
                  const selected = new Date(val);
                  return selected <= today || props.i18n.initial_balance_date_future_error;
                }
              ]"
            >
              <template v-slot:append>
                <q-icon
                  name="event"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    cover
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="selectedDate"
                      minimal
                      :options="date => {
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        return new Date(date) <= today;
                      }"
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          label="Close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="text-subtitle1 q-mt-lg q-mb-sm">
            {{ props.i18n.initial_balance_balances_title }}
          </div>

          <div
            v-if="availableAssets.length === 0"
            class="text-center q-pa-lg text-grey-7"
          >
            {{ props.i18n.initial_balance_loading_assets }}
          </div>

          <q-list
            v-else
            separator
            bordered
            class="rounded-borders"
          >
            <q-item
              v-for="asset in availableAssets"
              :key="asset.id"
              class="q-pa-md"
            >
              <q-item-section avatar>
                <q-avatar size="32px">
                  <img
                    :src="asset.logo_url || '/illustrations/asset_not_found.png'"
                    :alt="asset.symbol"
                  >
                </q-avatar>
              </q-item-section>

              <q-item-section>
                <q-item-label>{{ asset.symbol }}</q-item-label>
                <q-item-label caption>
                  {{ asset.name }}
                </q-item-label>
              </q-item-section>

              <q-item-section>
                <q-input
                  v-model="balances[asset.id]"
                  type="text"
                  filled
                  dense
                  style="width: 180px"
                  placeholder="0.00"
                  :label="props.i18n.initial_balance_balance_label.replace('%{symbol}', asset.symbol)"
                  @update:model-value="val => balances[asset.id] = val"
                />
              </q-item-section>

              <q-item-section>
                <q-input
                  v-model="fiatValues[asset.id]"
                  type="text"
                  filled
                  dense
                  style="width: 180px"
                  placeholder="0.00"
                  :label="props.i18n.initial_balance_fiat_value_label.replace('%{symbol}', props.fiatSymbol)"
                  @update:model-value="val => fiatValues[asset.id] = val"
                />
              </q-item-section>
            </q-item>
          </q-list>

          <q-card class="q-mt-md q-pa-md">
            <div class="row justify-between items-center">
              <div class="text-subtitle1">
                {{ props.i18n.initial_balance_total_balance }}
              </div>
              <div class="text-h6 text-bold">
                {{ formatTotalFiatValue() }}
              </div>
            </div>
          </q-card>

          <q-btn
            class="q-mt-md"
            flat
            color="primary"
            icon="add"
            :label="props.i18n.initial_balance_add_more_assets"
            @click="showAssetSelector = true"
          />
        </div>
      </q-card-section>

      <q-card-actions
        align="right"
        class="q-pa-md"
      >
        <q-btn
          flat
          :label="props.i18n.initial_balance_cancel_button"
          color="grey"
          @click="close"
        />
        <q-btn
          unelevated
          :label="props.i18n.initial_balance_create_balances_button"
          color="negative"
          :disable="!isValid"
          @click="confirmCreation"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <q-dialog
    v-model="confirmDialog"
    persistent
  >
    <q-card>
      <q-card-section class="row items-center">
        <q-avatar
          icon="warning"
          color="negative"
          text-color="white"
        />
        <span class="q-ml-sm">{{ props.i18n.initial_balance_confirm_title }} {{ props.i18n.initial_balance_confirm_message }}</span>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          flat
          :label="props.i18n.initial_balance_confirm_cancel"
          color="grey"
          @click="confirmDialog = false"
        />
        <q-btn
          flat
          :label="props.i18n.initial_balance_confirm_proceed"
          color="negative"
          @click="submitInitialBalances"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>

  <!-- Asset Selector Dialog -->
  <q-dialog
    v-model="showAssetSelector"
    persistent
  >
    <q-card style="min-width: 500px">
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">
          {{ props.i18n.initial_balance_select_asset }}
        </div>
        <q-space />
        <q-btn
          icon="close"
          flat
          round
          dense
          @click="showAssetSelector = false"
        />
      </q-card-section>

      <q-card-section>
        <q-input
          v-model="assetSearch"
          outlined
          dense
          :placeholder="props.i18n.initial_balance_search_assets"
          debounce="300"
          @update:model-value="searchAssets"
        >
          <template v-slot:prepend>
            <q-icon name="search" />
          </template>
        </q-input>

        <q-scroll-area
          style="height: 400px"
          class="q-mt-md"
        >
          <q-list separator>
            <q-item
              v-for="asset in searchedAssets"
              :key="asset.id"
              clickable
              @click="addAsset(asset)"
            >
              <q-item-section avatar>
                <q-avatar size="32px">
                  <img
                    :src="asset.logo_url"
                    :alt="asset.exchange_symbol"
                  >
                </q-avatar>
              </q-item-section>
              <q-item-section>
                <q-item-label>{{ asset.exchange_symbol }}</q-item-label>
                <q-item-label caption>
                  {{ asset.asset_name }}
                </q-item-label>
              </q-item-section>
            </q-item>

            <q-item v-if="searchedAssets.length === 0 && assetSearch">
              <q-item-section class="text-center text-grey-7">
                {{ props.i18n.initial_balance_no_assets_found }}
              </q-item-section>
            </q-item>
          </q-list>
        </q-scroll-area>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useForm } from '@inertiajs/vue3'
import axios from 'axios'

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  exchangeAccount: {
    type: Object,
    required: true
  },
  workspaceId: {
    type: String,
    required: true
  },
  fiatSymbol: {
    type: String,
    default: 'EUR'
  },
  i18n: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'submitted'])

const dialogOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

// Set default date to December 31st of previous year
const getDefaultDate = () => {
  const today = new Date()
  const previousYear = today.getFullYear() - 1
  return `${previousYear}-12-31`
}

const selectedDate = ref(getDefaultDate())
const balances = ref({})
const fiatValues = ref({})
const availableAssets = ref([])
const confirmDialog = ref(false)
const showAssetSelector = ref(false)
const assetSearch = ref('')
const searchedAssets = ref([])

const form = useForm({
  date: null,
  initial_balances: {}
})

const isValid = computed(() => {
  return selectedDate.value && Object.values(balances.value).some(v => {
    const val = parseFloat(v)
    return !isNaN(val) && val > 0
  })
})

const loadAssets = async () => {
  // Use assets from the exchange account data
  if (props.exchangeAccount.assets_table && props.exchangeAccount.assets_table.length > 0) {
    availableAssets.value = props.exchangeAccount.assets_table.map(item => ({
      id: item.exchange_asset_id || item.id,  // Use exchange_asset_id if available
      symbol: item.asset_symbol,
      name: item.asset_symbol,
      logo_url: item.asset_icon
    }))

    // Initialize balances and fiat values objects
    availableAssets.value.forEach(asset => {
      if (!balances.value[asset.id]) {
        balances.value[asset.id] = null
      }
      if (!fiatValues.value[asset.id]) {
        fiatValues.value[asset.id] = null
      }
    })
  }
}

const searchAssets = async (searchQuery) => {
  if (!props.exchangeAccount) return

  try {
    const response = await axios.get(`/w/${props.workspaceId}/accounts/${props.exchangeAccount.id}/exchange_assets`, {
      params: { search: searchQuery }
    })
    searchedAssets.value = response.data.exchange_assets || []
  } catch {
    searchedAssets.value = []
  }
}

const addAsset = (asset) => {
  // Check if asset is not already in the list
  const exists = availableAssets.value.find(a => a.id === asset.id)
  if (!exists) {
    availableAssets.value.push({
      id: asset.id,
      symbol: asset.exchange_symbol,
      name: asset.asset_name,
      logo_url: asset.logo_url
    })

    // Initialize balance and fiat value for the new asset
    balances.value[asset.id] = null
    fiatValues.value[asset.id] = null
  }

  showAssetSelector.value = false
  assetSearch.value = ''
}

const formatTotalFiatValue = () => {
  let total = 0
  availableAssets.value.forEach(asset => {
    const fiatValue = parseFloat(fiatValues.value[asset.id]) || 0
    total += fiatValue
  })
  return `${props.fiatSymbol} ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const close = () => {
  dialogOpen.value = false
  selectedDate.value = getDefaultDate()
  balances.value = {}
  fiatValues.value = {}
  confirmDialog.value = false
  showAssetSelector.value = false
  assetSearch.value = ''
  searchedAssets.value = []
}

const confirmCreation = () => {
  if (!isValid.value) return
  confirmDialog.value = true
}

const submitInitialBalances = () => {
  // Prepare data with both balances and fiat amounts
  const initialBalancesData = {}
  Object.entries(balances.value).forEach(([assetId, balance]) => {
    // Convert balance to string and check if it's not empty or zero
    const balanceStr = String(balance || '').trim()
    if (balanceStr && balanceStr !== '0' && balanceStr !== '0.0') {
      const fiatValueStr = String(fiatValues.value[assetId] || '0').trim()
      initialBalancesData[assetId] = {
        amount: balanceStr,
        fiat_amount: fiatValueStr || '0'
      }
    }
  })

  form.date = selectedDate.value
  form.initial_balances = initialBalancesData

  form.post(`/w/${props.workspaceId}/accounts/${props.exchangeAccount.id}/create_initial_balance`, {
    preserveScroll: true,
    onSuccess: () => {
      emit('submitted')
      close()
    },
    onError: () => {
      confirmDialog.value = false
    }
  })
}

onMounted(() => {
  if (dialogOpen.value) {
    loadAssets()
    // Load all available assets for the first time
    searchAssets('')
  }
})

watch(dialogOpen, (newVal) => {
  if (newVal) {
    loadAssets()
    // Load all available assets for the first time
    searchAssets('')
  }
})
</script>

<style scoped>
.q-item {
  transition: background-color 0.3s;
}

.q-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>